import {
  CmpasModuleSectionDetail,
  useCommonCmpasModuleSectionRetrieveQuery,
  useCommonCmpasModuleSectionCreateMutation,
  useCommonCmpasModuleSectionUpdateMutation,
  useCommonCmpasModuleListQuery,
} from "@app/api/carApi.generated";
import { useForm } from "./useForm";
import { FormEditorDialog } from "./FormEditorDialog";
import { CarSelectOption } from "@app/components/Inputs";
import { checkDefined, sortBySortOrder } from "@app/utils";
import { CarPageTreeItem, usePageTree } from "@app/hooks/usePageTree";

interface ModuleSectionEditorDialogProps {
  id?: string;
  moduleId: string;
  nextSortOrder?: number;
  onApply: () => void;
  onCancel: () => void;
}

export const ModuleSectionEditorDialog = (
  props: ModuleSectionEditorDialogProps,
) => {
  const query = useCommonCmpasModuleSectionRetrieveQuery(
    {
      id: props.id ?? "",
    },
    { skip: !props.id },
  );

  const [create] = useCommonCmpasModuleSectionCreateMutation();
  const [update] = useCommonCmpasModuleSectionUpdateMutation();

  const modules = useCommonCmpasModuleListQuery();
  const pages = usePageTree();

  const pageOptions: CarSelectOption<string>[] = [];

  const addToOptions = (item: CarPageTreeItem, level: number) => {
    pageOptions.push({
      label: item.label, //`${item.label} (${item.code})`,
      value: item.id,
      level,
      isBold: level === 0,
    });

    item.children.forEach((i) => addToOptions(i as CarPageTreeItem, level + 1));
  };

  pages.items.forEach((i) => addToOptions(i, 0));

  const form = useForm<CmpasModuleSectionDetail>({
    entityLabel: "Page",
    getItemName: (item) => `${item.name} (${item.code})`,
    isLoading: query.isLoading || modules.isLoading,
    item: query.data,
    getNewItem: () => ({
      code: "",
      name: "",
      cmpas_module: props.moduleId,
      is_active: true,
      sort_order: props.nextSortOrder,
    }),
    onClose: props.onCancel,
    onCreate: (item) =>
      create({
        cmpasModuleSectionDetail: item,
      }),

    onUpdate: (item) =>
      update({
        id: item.id ?? "",
        cmpasModuleSectionDetail: item,
      }),
    onDelete: async (item) => {
      // todo
    },
    fieldDefs: [
      {
        key: "code",
        type: "string",
        label: "Code",
        description: "Unique code for the page",
        isRequired: true,
      },
      {
        key: "name",
        type: "string",
        label: "Name",
        description: "Name of the page",
        isRequired: true,
      },
      {
        key: "description",
        type: "memo",
        label: "Description",
        description: "Description of the page",
      },
      {
        key: "parent_page",
        type: "select",
        options: pageOptions,
        label: "Parent",
        description: "The parent of this page",
        isRequired: false,
        canClear: true,
      },
      {
        key: "cmpas_module",
        type: "select",
        options:
          sortBySortOrder(modules.data)
            .filter((i) => i.is_active)
            .map<CarSelectOption<string>>((i) => ({
              label: `${i.name} (${i.code})`,
              value: checkDefined(i.id),
            })) ?? [],
        label: "Module",
        description: "The app module this page belongs to",
        isRequired: true,
      },
      {
        key: "front_end_url",
        type: "string",
        label: "Front End Url",
        description:
          "The front end URL for this page. It can include placeholders for dynamic data",
      },
      {
        key: "is_active",
        type: "boolean",
        label: "Is Active",
        description: "Is this page active",
      },
      {
        key: "sort_order",
        type: "integer",
        label: "Sort Order",
        description: "Sort order of page",
      },
    ],
  });

  return <FormEditorDialog form={form} />;
};
